'use client'

import { useAnchoredDropdown } from '@/components/use-anchored-dropdown'
import { searchTussCodes, TussCode } from '@/lib/api/tuss/tuss-api'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdClose, MdHealthAndSafety, MdSearch } from 'react-icons/md'

interface Props {
    value: string | null
    onChange: (code: string | null, tuss: TussCode | null) => void
    label?: string
    placeholder?: string
    disabled?: boolean
    className?: string
    size?: 'sm' | 'md'
}

export function TussSelector({
    value,
    onChange,
    label = 'Código TUSS',
    placeholder = 'Buscar código ou termo TUSS...',
    disabled = false,
    className = '',
    size = 'md',
}: Props) {
    const [results, setResults] = useState<TussCode[]>([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [selected, setSelected] = useState<TussCode | null>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const searchByTerm = useCallback(async (term: string) => {
        setLoading(true)
        try {
            const response = await searchTussCodes(term || '', 20)
            if (response.success) {
                setResults(Array.isArray(response.result) ? response.result : [])
            }
        } catch {
            // silencioso
        } finally {
            setLoading(false)
        }
    }, [])

    // Sincroniza o item exibido com o `value` (código). Resolve o termo via API
    // quando temos só o código.
    useEffect(() => {
        if (!value) {
            setSelected(null)
            return
        }
        if (selected?.code === value) return
        searchTussCodes(value, 5).then(response => {
            if (response.success) {
                const found = (response.result || []).find(t => t.code === value)
                if (found) setSelected(found)
            }
        })
    }, [value, selected])

    // Debounce na digitação
    useEffect(() => {
        if (!showDropdown) return
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => searchByTerm(searchTerm), 300)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [searchTerm, showDropdown, searchByTerm])

    /**
     * O painel sai do fluxo (portal no body, posição fixa).
     *
     * Com `absolute` ele era cortado sempre que um ancestral tinha overflow — o
     * cadastro rápido do catálogo abre dentro de um modal com rolagem, e a lista de
     * códigos ficava escondida atrás da borda. Ver `useAnchoredDropdown`.
     */
    const closeDropdown = useCallback(() => setShowDropdown(false), [])
    const { anchorRef, renderPanel } = useAnchoredDropdown(
        showDropdown && !selected,
        closeDropdown
    )

    const handleFocus = () => {
        setShowDropdown(true)
        if (results.length === 0) searchByTerm('')
    }

    const handleSelect = (tuss: TussCode) => {
        setSelected(tuss)
        onChange(tuss.code, tuss)
        setShowDropdown(false)
        setSearchTerm('')
    }

    const handleClear = () => {
        setSelected(null)
        onChange(null, null)
        setSearchTerm('')
    }

    return (
        <div className={`form-control w-full ${className}`}>
            {label && (
                <label className="label">
                    <span className="label-text">{label}</span>
                </label>
            )}

            <div className="relative" ref={anchorRef}>
                {selected ? (
                    <div
                        className={
                            'flex items-center gap-2 input input-bordered w-full pr-10 ' +
                            (size === 'sm' ? 'input-sm!' : '')
                        }
                    >
                        <MdHealthAndSafety className="text-lg text-secondary flex-shrink-0" />
                        <span className="truncate">
                            <span className="font-mono">{selected.code}</span> —{' '}
                            {selected.term}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                            >
                                <MdClose className="text-lg" />
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl pointer-events-none" />
                        <input
                            type="text"
                            placeholder={placeholder}
                            className={
                                'input input-bordered w-full pl-10 ' +
                                (size === 'sm' ? 'input-sm!' : '')
                            }
                            value={searchTerm}
                            onChange={e => {
                                setSearchTerm(e.target.value)
                                setShowDropdown(true)
                            }}
                            onFocus={handleFocus}
                            disabled={disabled}
                            autoComplete="off"
                            aria-autocomplete="none"
                        />
                    </>
                )}

                {renderPanel(
                    <>
                        {loading ? (
                            <div className="p-4 text-center">
                                <span className="loading loading-spinner loading-sm"></span>
                            </div>
                        ) : results.length === 0 ? (
                            <div className="p-4 text-center text-base-content/60 text-sm">
                                {searchTerm
                                    ? 'Nenhum código TUSS encontrado'
                                    : 'Digite para buscar'}
                            </div>
                        ) : (
                            results.map(tuss => (
                                <button
                                    key={tuss.id}
                                    type="button"
                                    className="w-full px-4 py-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-200 last:border-b-0"
                                    onClick={() => handleSelect(tuss)}
                                >
                                    <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                        <MdHealthAndSafety className="text-secondary" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium truncate">
                                            {tuss.term}
                                        </p>
                                        <p className="text-xs text-base-content/60 truncate font-mono">
                                            {tuss.code}
                                        </p>
                                    </div>
                                </button>
                            ))
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
